/**
 * The SPARK brand mark (the bolt) with the product wordmark.
 *
 * One component for every place the product identifies itself: the top bar,
 * the mobile drawer, the login screen, report covers, the public share header,
 * and the running headers on printed report pages. The bolt is inlined rather
 * than loaded from /spark-logo.svg so it prints, works inside email-safe
 * markup, and never flashes in after the text.
 *
 * Brand rules honored here: the bolt stays SPARK Green on every surface, is
 * never recolored or rotated, and sits with clear space beside the wordmark.
 */

type Tone = "light" | "dark" | "navigation";
type Size = "xs" | "sm" | "md" | "lg";

const BOLT_PATH =
  "M178.46,259.53H306.39a6.68,6.68,0,0,1,5.37,10.55L125.58,551c-1.53,3-6.14.76-5.37-2.25l46-217.63H43.62a6.67,6.67,0,0,1-5.37-10.55L222.11,48.68c.78-2.26,5.37-.74,4.6,3Z";

const SIZES: Record<Size, { bolt: number; name: string; product: string; gap: string }> = {
  xs: { bolt: 14, name: "text-[11px] tracking-wide", product: "text-[11px]", gap: "gap-1.5" },
  sm: { bolt: 18, name: "text-sm tracking-wide", product: "text-xs", gap: "gap-2" },
  md: { bolt: 22, name: "text-lg tracking-wide", product: "text-sm", gap: "gap-2" },
  lg: { bolt: 34, name: "text-2xl tracking-tight", product: "text-lg", gap: "gap-3" },
};

const TONES: Record<Tone, { name: string; product: string }> = {
  navigation: { name: "text-spark-navigation-strong", product: "text-spark-navigation-text" },
  light: { name: "text-spark-black", product: "text-spark-gray" },
  dark: { name: "text-white", product: "text-[#ADADB3]" },
};

export function BrandBolt({ size = 22, className }: { size?: number; className?: string }) {
  // The source artwork is 350 x 600; keep the aspect so the bolt never skews.
  const width = Math.round((size * 350) / 600);
  return (
    <svg
      viewBox="0 0 350 600"
      width={width}
      height={size}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path fill="#21D679" d={BOLT_PATH} />
    </svg>
  );
}

export function BrandMark({
  tone = "light",
  size = "md",
  wordmark = true,
  product = "Dash",
  className,
}: {
  /** The surface the mark sits on: "dark" for the chrome, "light" for paper and cards */
  tone?: Tone;
  size?: Size;
  /** Bolt only when false (the collapsed rail) */
  wordmark?: boolean;
  /** Product name after SPARK; pass null for the bare company mark */
  product?: string | null;
  className?: string;
}) {
  const s = SIZES[size];
  const t = TONES[tone];
  return (
    <span className={`inline-flex shrink-0 items-center ${s.gap} ${className ?? ""}`}>
      <BrandBolt size={s.bolt} />
      {wordmark && (
        <>
          <span className={`font-extrabold leading-none ${s.name} ${t.name}`}>SPARK</span>
          {product && <span className={`font-medium leading-none ${s.product} ${t.product}`}>{product}</span>}
        </>
      )}
    </span>
  );
}
